import { createContext, useContext, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { Company } from '@/types/company';
import { useAuth } from '@/context/AuthContext';
import { setSentryUser, setSentryViewMode } from '@/lib/sentry';
import { setCompany } from '@/lib/analytics';

interface CompanyContextType {
  company: Company | null;
  loading: boolean;
  error: string | null;
  isPlatformAdminView: boolean;
  refetch: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

/** Exported so callers that mutate company membership can invalidate exactly this query. */
export const companyQueryKey = (userId: string | undefined) => ['company', userId] as const;

export const CompanyProvider = ({ children }: { children: ReactNode }) => {
  const { user, platformRole, loading: authLoading } = useAuth();
  const userId = user?.id;

  // Keyed on user?.id (a string), NOT the `user` object. The previous version was a
  // useCallback([user]) + useEffect pair, so any new User reference — and AuthContext handed
  // out a fresh one on every navigation — issued another company-api round trip. React Query
  // also collapses concurrent callers of the same key into one in-flight request, so this is
  // deduped by construction rather than by every consumer remembering to share. FIN-608:
  // one /join-company load made 19 of these calls in 2 seconds.
  const {
    data: company = null,
    isPending,
    error,
    refetch: refetchQuery,
  } = useQuery({
    queryKey: companyQueryKey(userId),
    queryFn: () => ServiceRegistry.getCompanyService().getCompany(),
    enabled: !!userId,
  });

  useEffect(() => {
    if (userId && company) setSentryUser(userId, company.id);
  }, [userId, company]);

  // `loading` must mean "we don't know yet", which is what the previous implementation's
  // useState(true) initial value conveyed. While auth is still resolving there is no userId, so
  // the query is disabled and isPending alone would report loading:false with company:null —
  // i.e. "this user has no company", which consumers act on. Keep auth's own resolution inside
  // it so that state is never briefly published as fact.
  const loading = authLoading || (!!userId && isPending);
  const isPlatformAdminView = platformRole === 'platform_admin' && company === null;

  // Gated on `!loading` because `company` is null while the query is in flight, which would
  // otherwise tag an admin's ordinary single-company session as admin view for a moment.
  useEffect(() => {
    if (loading) return;
    setSentryViewMode(isPlatformAdminView ? 'platform_admin' : null);
  }, [loading, isPlatformAdminView]);

  const companyId = company?.id ?? null;
  useEffect(() => {
    if (loading) return;
    setCompany(companyId);
  }, [loading, companyId]);

  const refetch = useCallback(async () => {
    await refetchQuery();
  }, [refetchQuery]);

  const value = useMemo(
    () => ({
      company,
      loading,
      error: error?.message ?? null,
      isPlatformAdminView,
      refetch,
    }),
    [company, loading, error, isPlatformAdminView, refetch],
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};
